import { MessageSeverity } from "../enums/message-severity";
import { AvatarPrimitive } from "../models/app/avatar-primitive";
import { MessageContact } from "../models/data/messaging/message-contact";
import { ActingProfile } from "../models/data/talent-core/acting-profile";
import { ActingProfileCharacterType } from "../models/data/talent-core/acting-profile-character-type";
import { ActingProfileEthnicity } from "../models/data/talent-core/acting-profile-ethnicity";
import { Engagement } from "../models/data/talent-core/engagement";
import { EngagementStatus } from "../models/data/talent-core/engagement-status";
import { ProductionRole } from "../models/data/talent-core/production-role";
import { ActingProfileEnvelope } from "../models/net/envelope/acting-profile-envelope";
import { ActingProfileDocument } from "../models/net/envelope/documents/acting-profile-document";
import { EngagementDocument } from "../models/net/envelope/documents/engagement-document";
import { EngagementEnvelope } from "../models/net/envelope/engagement-envelope";
import { ProductionEnvelope } from "../models/net/envelope/production-envelope";
import { ResourceEnvelope } from "../models/net/envelope/resource-envelope";
import { EngagementSearchResult } from "../models/net/result/engagement-search-result";
import { ProductionSearchResult } from "../models/net/result/production-search-result";
import { ResourceSearchResult } from "../models/net/result/resource-search-result";
import { AppService } from "../services/app.service";
import { EngagementService } from "../services/engagements/engagement.service";

export class EnvelopeFactory {

    private constructor() { }

    public static extractActingProfileEnvelope(actingProfileId: number, resourceEnvelope: ResourceEnvelope, appService: AppService): ActingProfileEnvelope {

        let result: ActingProfileEnvelope = new ActingProfileEnvelope(null);

        result.Resources = appService.cloneObject(resourceEnvelope);
        result.Genders = appService.cloneObject(resourceEnvelope.Genders);
        result.CharacterTypes = appService.cloneObject(resourceEnvelope.CharacterTypes);
        result.Ethnicities = appService.cloneObject(resourceEnvelope.Ethnicities);

        let actingProfileDocument: ActingProfileDocument = new ActingProfileDocument();

        const characterTypes: ActingProfileCharacterType[] = resourceEnvelope.Document.ProfileCharacterTypes.filter((i: ActingProfileCharacterType) => i.ActingProfileId == actingProfileId);
        const ethnicities: ActingProfileEthnicity[] = resourceEnvelope.Document.ProfileEthnicities.filter((i: ActingProfileEthnicity) => i.ActingProfileId == actingProfileId);

        actingProfileDocument.CharacterTypes = appService.cloneObject(characterTypes);
        actingProfileDocument.Ethnicities = appService.cloneObject(ethnicities);

        const sourceActingProfiles = resourceEnvelope.Document.ActingProfiles.filter((i: ActingProfile) => i.ActingProfileId == actingProfileId);

        if (sourceActingProfiles && sourceActingProfiles.length == 1) {

            let actingProfile: ActingProfile = new ActingProfile();

            actingProfile.ActingProfileId = actingProfileId;
            actingProfile.ResourceId = sourceActingProfiles[0].ResourceId;
            actingProfile.GenderId = sourceActingProfiles[0].GenderId;
            actingProfile.Name = sourceActingProfiles[0].Name;
            actingProfile.Description = sourceActingProfiles[0].Description;
            actingProfile.Pitch = sourceActingProfiles[0].Pitch;
            actingProfile.AgeRangeYearsLower = sourceActingProfiles[0].AgeRangeYearsLower;
            actingProfile.AgeRangeYearsUpper = sourceActingProfiles[0].AgeRangeYearsUpper;
            actingProfile.AvatarBlobPath = sourceActingProfiles[0].AvatarBlobPath;

            actingProfileDocument.Item = actingProfile;
        }

        result.Document = actingProfileDocument;

        return result;
    }

    public static assembleEngagementEnvelope(engagement: Engagement, proformaEngagementEnvelope: EngagementEnvelope, appService: AppService): EngagementEnvelope {

        let result: EngagementEnvelope = appService.cloneObject(proformaEngagementEnvelope);

        // this is a psuedo-extraction because the resource envelope is not rich enough in data to pull out an engagement envelope (unlike an acting profile envelope) so,
        // the caller provides a new 'proforma' envelope which contains the meta data lacking in the resource envelope from which point the extraction can begin
        result.Document.Item.ProductionId = engagement.ProductionId;
        result.Document.Item.EngagementId = engagement.EngagementId;
        result.Document.Item.ResourceId = engagement.ResourceId;
        result.Document.Item.ProductionRoleId = engagement.ProductionRoleId;
        result.Document.Item.EngagementStatusId = engagement.EngagementStatusId;
        result.Document.Item.AvatarBlobPath = engagement.AvatarBlobPath;
        result.Document.Item.StartOnUTC = engagement.StartOnUTC;
        result.Document.Item.GeoLocation = undefined;

        return result;
    }

    public static mergeActingProfileToResourceEnvelope(actingProfileEnvelope: ActingProfileEnvelope, resourceEnvelope: ResourceEnvelope, appService: AppService): ResourceEnvelope {

        let result: ResourceEnvelope = resourceEnvelope;
        let existingActingProfiles: ActingProfile[] = result.Document.ActingProfiles.filter((i: ActingProfile) => i.ActingProfileId == actingProfileEnvelope.Document.Item.ActingProfileId);

        if (existingActingProfiles && existingActingProfiles.length == 1) {

            let actingProfile: ActingProfile = existingActingProfiles[0];

            actingProfile.GenderId = actingProfileEnvelope.Document.Item.GenderId;
            actingProfile.Name = actingProfileEnvelope.Document.Item.Name;
            actingProfile.Description = actingProfileEnvelope.Document.Item.Description;
            actingProfile.Pitch = actingProfileEnvelope.Document.Item.Pitch;
            actingProfile.AgeRangeYearsLower = actingProfileEnvelope.Document.Item.AgeRangeYearsLower;
            actingProfile.AgeRangeYearsUpper = actingProfileEnvelope.Document.Item.AgeRangeYearsUpper;
            actingProfile.AvatarBlobPath = actingProfileEnvelope.Document.Item.AvatarBlobPath;

            const preserveCharacterTypes: ActingProfileCharacterType[] = resourceEnvelope.Document.ProfileCharacterTypes.filter((i: ActingProfileCharacterType) => i.ActingProfileId != actingProfileEnvelope.Document.Item.ActingProfileId);
            result.Document.ProfileCharacterTypes = [...preserveCharacterTypes, ...actingProfileEnvelope.Document.CharacterTypes];

            const preserveEthnicities: ActingProfileEthnicity[] = resourceEnvelope.Document.ProfileEthnicities.filter((i: ActingProfileEthnicity) => i.ActingProfileId != actingProfileEnvelope.Document.Item.ActingProfileId);
            result.Document.ProfileEthnicities = [...preserveEthnicities, ...actingProfileEnvelope.Document.Ethnicities];

        } else if (actingProfileEnvelope.Document.Item.ActingProfileId < 0) { // new profile

            result.Document.ActingProfiles.push(actingProfileEnvelope.Document.Item);
            result.Document.ProfileCharacterTypes.push(...actingProfileEnvelope.Document.CharacterTypes);
            result.Document.ProfileEthnicities.push(...actingProfileEnvelope.Document.Ethnicities);

        } else {

            const dataStateDescription: string = existingActingProfiles ? "Duplicated Profiles" : "Profile not found";
            appService.showDialog(MessageSeverity.Warning, "Cannot Remove Acting Profile", "Cannot Remove Acting Profile named " + actingProfileEnvelope.Document.Item.Name + " cannot be processed due to data state as " + dataStateDescription, "OK", "", true, true);
        }

        return result;
    }

    public static mergeEngagementToResourceEnvelope(engagementEnvelope: EngagementEnvelope, resourceEnvelope: ResourceEnvelope): ResourceEnvelope {

        let result: ResourceEnvelope = resourceEnvelope;
        let existingEngagements: Engagement[] = result.Document.Engagements.filter((i: Engagement) => i.EngagementId == engagementEnvelope.Document.Item.EngagementId);

        if (existingEngagements && existingEngagements.length == 1) {

            let engagement: Engagement = existingEngagements[0];

            engagement.ResourceId = engagementEnvelope.Document.Item.ResourceId;
            engagement.ProductionId = engagementEnvelope.Document.Item.ProductionId;
            engagement.ProductionRoleId = engagementEnvelope.Document.Item.ProductionRoleId;
            engagement.StartOnUTC = engagementEnvelope.Document.Item.StartOnUTC;
            engagement.EngagementStatusId = engagementEnvelope.Document.Item.EngagementStatusId;

        } else if (engagementEnvelope.Document.Item.EngagementId < 0) { // new engagement

            let newEngagement: Engagement = new Engagement();

            newEngagement.EngagementId = engagementEnvelope.Document.Item.EngagementId;
            newEngagement.ResourceId = engagementEnvelope.Document.Item.ResourceId;
            newEngagement.ProductionId = engagementEnvelope.Document.Item.ProductionId;
            newEngagement.ProductionRoleId = engagementEnvelope.Document.Item.ProductionRoleId
            newEngagement.StartOnUTC = engagementEnvelope.Document.Item.StartOnUTC;
            newEngagement.EngagementStatusId = engagementEnvelope.Document.Item.EngagementStatusId;

            result.Document.Engagements.push(newEngagement);
        }

        return result;
    }

    public static mergeEngagementoToProductionEnvelope(engagementEnvelope: EngagementEnvelope, productionEnvelope: ProductionEnvelope): ProductionEnvelope {

        let result: ProductionEnvelope = productionEnvelope;
        let existingEngagements: Engagement[] = result.Document.Engagements.filter((i: Engagement) => i.EngagementId == engagementEnvelope.Document.Item.EngagementId);

        if (existingEngagements && existingEngagements.length == 1) {

            let engagement: Engagement = existingEngagements[0];

            engagement.ResourceId = engagementEnvelope.Document.Item.ResourceId;
            engagement.ProductionId = engagementEnvelope.Document.Item.ProductionId;
            engagement.ProductionRoleId = engagementEnvelope.Document.Item.ProductionRoleId;
            engagement.StartOnUTC = engagementEnvelope.Document.Item.StartOnUTC;
            engagement.EngagementStatusId = engagementEnvelope.Document.Item.EngagementStatusId;

        } else if (engagementEnvelope.Document.Item.EngagementId < 0) { // new engagement

            let newEngagement: Engagement = new Engagement();

            newEngagement.EngagementId = engagementEnvelope.Document.Item.EngagementId;
            newEngagement.ResourceId = engagementEnvelope.Document.Item.ResourceId;
            newEngagement.ProductionId = engagementEnvelope.Document.Item.ProductionId;
            newEngagement.ProductionRoleId = engagementEnvelope.Document.Item.ProductionRoleId
            newEngagement.StartOnUTC = engagementEnvelope.Document.Item.StartOnUTC;
            newEngagement.EngagementStatusId = engagementEnvelope.Document.Item.EngagementStatusId;

            result.Document.Engagements.push(newEngagement);
        }

        return result;
    }

    public static removeActingProfileEnvelope(removeActingProfile: ActingProfile, resourceEnvelope: ResourceEnvelope, appService: AppService): ResourceEnvelope {

        let result: ResourceEnvelope = resourceEnvelope;
        let removeActingProfileEnvelope: ActingProfileEnvelope = this.extractActingProfileEnvelope(removeActingProfile.ActingProfileId, resourceEnvelope, appService);
        let existingActingProfiles: ActingProfile[] = result.Document.ActingProfiles.filter((i: ActingProfile) => i.ActingProfileId == removeActingProfileEnvelope.Document.Item.ActingProfileId);

        if (existingActingProfiles && existingActingProfiles.length == 1) {

            let removeActingProfile: ActingProfile = existingActingProfiles[0];

            result.Document.ProfileCharacterTypes = result.Document.ProfileCharacterTypes.filter((characterType: ActingProfileCharacterType) => {

                const isRemovable = removeActingProfileEnvelope.Document.CharacterTypes.some((removeCharacterType: ActingProfileCharacterType) => {

                    const isRemovable: boolean = removeCharacterType.ActingProfileId === characterType.ActingProfileId;

                    return isRemovable;
                });

                return !isRemovable;
            });

            result.Document.ProfileEthnicities = result.Document.ProfileEthnicities.filter((ethnicity: ActingProfileEthnicity) => {

                const isRemovable = removeActingProfileEnvelope.Document.Ethnicities.some((removeEthnicity: ActingProfileEthnicity) => {

                    const isRemovable: boolean = removeEthnicity.ActingProfileId === ethnicity.ActingProfileId;

                    return isRemovable;
                });

                return !isRemovable;
            });

            result.Document.ActingProfiles = result.Document.ActingProfiles.filter((i: ActingProfile) => i.ActingProfileId !== removeActingProfile.ActingProfileId);

        } else {

            const dataStateDescription: string = existingActingProfiles ? "Duplicated Profiles" : "Profile not found";
            appService.showDialog(MessageSeverity.Warning, "Cannot Add or Merge Acting Profile", "Cannot Add or Merge Acting Profile named " + removeActingProfile.Name + " cannot be processed due to data state as " + dataStateDescription, "OK", "", true, true);
        }

        return result;
    }

    public static removeEngagementFromResource(removeEngagement: Engagement, resourceEnvelope: ResourceEnvelope, appService: AppService): ResourceEnvelope {

        let result: ResourceEnvelope = resourceEnvelope;

        result.Document.Engagements = result.Document.Engagements.filter((i: Engagement) => i.EngagementId !== removeEngagement.EngagementId);

        return result;
    }

    public static removeEngagementFromProduction(removeEngagement: Engagement, productionEnvelope: ProductionEnvelope, appService: AppService): ProductionEnvelope {

        let result: ProductionEnvelope = productionEnvelope;

        result.Document.Engagements = result.Document.Engagements.filter((i: Engagement) => i.EngagementId !== removeEngagement.EngagementId);

        return result;
    }

    public static getProformaActingProfileId(resourceEnvelope: ResourceEnvelope): number {

        let result: number = 0; // the final result should always be the next lowest negative number

        const actingProfiles: ActingProfile[] = resourceEnvelope?.Document.ActingProfiles;

        if (actingProfiles.length > 0) {

            result = actingProfiles.reduce((min, i) => (i.ActingProfileId < min ? i.ActingProfileId : min), actingProfiles[0].ActingProfileId);

            if (result > 0) {

                result = 0;
            }
        }

        result--; // decrement the lowest to the next lowest actingprofileid

        return result;
    }

    public static getProformaEngagementId(engagements: Engagement[]): number {

        let result: number = 0; // the final result should always be the next lowest negative number

        if (engagements.length > 0) {

            result = engagements.reduce((min, i) => (i.EngagementId < min ? i.EngagementId : min), engagements[0].EngagementId);

            if (result > 0) {

                result = 0;
            }
        }

        result--; // decrement the lowest to the next lowest actingprofileid

        return result;
    }

    public static getProformaMessageContactId(messageContacts: MessageContact[]): number {

        let result: number = 0;

        if (messageContacts.length > 0) {

            result = messageContacts.reduce((min, i) => (i.MessageContactId < min ? i.MessageContactId : min), messageContacts[0].MessageContactId);
;
            if (result > 0) {

                result = 0;
            }
        }

        result--;

        return result;
    }
}
